<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import {
  CAMPAIGN_AUDIENCE_TYPES,
  CAMPAIGN_TYPES,
} from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import { buildCampaignAudience } from 'shared/helpers/campaignHelper';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  buildTemplateParameters,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';
import {
  VARIABLE_SOURCES,
  columnsInLiquid,
  normalizeColumnName,
  splitVariables,
  variableContext,
  variableExample,
  variableToLiquid,
} from 'dashboard/helper/campaignVariables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import CampaignAudienceField from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignAudienceField.vue';
import CampaignFormSection from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignFormSection.vue';
import CampaignScheduleField from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignScheduleField.vue';
import WhatsAppCampaignPreview from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignPreview.vue';
import WhatsAppCampaignVariable from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignVariable.vue';

// Named for the campaigns view, which keeps this page out of its cache: each visit starts blank.
defineOptions({ name: 'WhatsAppCampaignNewPage' });

const PREVIEW_DEBOUNCE_MS = 500;

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const whatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const allInboxes = useMapGetter('inboxes/getInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const state = reactive({
  title: '',
  inboxId: null,
  templateId: null,
  message: '',
  mediaUrl: '',
  mediaName: '',
  scheduledAt: '',
  audienceType: CAMPAIGN_AUDIENCE_TYPES.UPLOAD,
  selectedLabels: [],
  selectedAudienceInboxId: null,
  uploadedContactIds: [],
  uploadedColumns: [],
});

// One entry per variable of the template, with where its value comes from.
const variables = ref([]);
const messageTextAreaRef = ref(null);

const comboBoxClass =
  '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6';

const toOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const inboxOptions = computed(() =>
  toOptions(whatsAppInboxes.value, 'id', 'name')
);
const audienceInboxOptions = computed(() =>
  toOptions(allInboxes.value, 'id', 'name')
);
const labelOptions = computed(() => toOptions(labels.value, 'id', 'title'));

// Evolution has no message templates: its campaigns go out as a plain text message instead.
const isEvolutionInbox = computed(
  () =>
    whatsAppInboxes.value.find(inbox => inbox.id === state.inboxId)
      ?.provider === 'evolution'
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  return getFilteredWhatsAppTemplates.value(state.inboxId).map(template => ({
    value: template.id,
    label: `${template.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} (${template.language || 'en'})`,
    template,
  }));
});

const selectedTemplate = computed(
  () =>
    templateOptions.value.find(option => option.value === state.templateId)
      ?.template ?? null
);

const templateComponent = type =>
  selectedTemplate.value
    ? findComponentByType(selectedTemplate.value, type)
    : null;

const bodyText = computed(
  () => templateComponent(COMPONENT_TYPES.BODY)?.text || ''
);
const mediaFormat = computed(() => {
  const format = templateComponent(COMPONENT_TYPES.HEADER)?.format;
  return MEDIA_FORMATS.includes(format) ? format.toLowerCase() : '';
});

const sourceOptions = computed(() => [
  {
    value: VARIABLE_SOURCES.NAME,
    label: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCES.NAME'),
  },
  {
    value: VARIABLE_SOURCES.FIRST_NAME,
    label: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCES.FIRST_NAME'),
  },
  {
    value: VARIABLE_SOURCES.PHONE,
    label: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCES.PHONE'),
  },
  {
    value: VARIABLE_SOURCES.EMAIL,
    label: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCES.EMAIL'),
  },
  {
    value: VARIABLE_SOURCES.COLUMN,
    label: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCES.COLUMN'),
  },
  {
    value: VARIABLE_SOURCES.TEXT,
    label: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCES.TEXT'),
  },
]);

const sourceLabel = source =>
  sourceOptions.value.find(option => option.value === source)?.label || '';

const columnChip = column =>
  t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.COLUMN_CHIP', { column });

const isUploadAudience = computed(
  () => state.audienceType === CAMPAIGN_AUDIENCE_TYPES.UPLOAD
);

// What a variable is called in the spreadsheet: a named one keeps its name, a numbered one becomes
// "variavel_2" ("cabecalho_1" and "botao_1" in the header and on a button).
const defaultColumn = ({ section, number }) => {
  if (!/^\d+$/.test(number)) return normalizeColumnName(number);
  if (section === 'header') {
    return t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.DEFAULT_COLUMN.HEADER', {
      number,
    });
  }
  if (section === 'buttons') {
    return t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.DEFAULT_COLUMN.BUTTON', {
      number,
    });
  }
  return t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.DEFAULT_COLUMN.BODY', {
    number,
  });
};

// With a spreadsheet as the audience every variable starts out as a column of it, so the Excel to
// download already has one column per variable of the template. One already set is left alone.
const linkToSpreadsheet = variable => {
  if (variable.source || !isUploadAudience.value) return;
  variable.source = VARIABLE_SOURCES.COLUMN;
  variable.column = defaultColumn(variable);
};

// Keyed on the id, not the template object: the inbox list is fetched again whenever its cache is
// invalidated, and a new object for the same template must not wipe what was filled in.
watch(
  () => state.templateId,
  () => {
    const template = selectedTemplate.value;
    state.mediaUrl = '';
    state.mediaName = '';
    if (!template) {
      variables.value = [];
      return;
    }

    const scaffold = buildTemplateParameters(template);
    const header = findComponentByType(template, COMPONENT_TYPES.HEADER);
    const templateButtons =
      findComponentByType(template, COMPONENT_TYPES.BUTTONS)?.buttons || [];
    const entry = (section, key, text, extra = {}) => ({
      id: `${section}.${key}`,
      section,
      key,
      number: key,
      label: `{{${key}}}`,
      example: variableExample(template, { section, key }),
      ...variableContext(text, key),
      source: '',
      column: '',
      text: '',
      ...extra,
    });

    const headerKeys =
      header?.format === 'TEXT' ? Object.keys(scaffold.header || {}) : [];
    const buttonEntries = [];
    (scaffold.buttons || []).forEach((button, index) => {
      const caption = t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.BUTTON_CAPTION', {
        button: templateButtons[index]?.text || '',
      });
      const example = variableExample(template, {
        section: 'buttons',
        buttonIndex: index,
      });
      buttonEntries.push(
        button.type === 'url'
          ? entry('buttons', index, button.url, {
              number: button.variables[0],
              label: `{{${button.variables[0]}}}`,
              example,
              ...variableContext(button.url, button.variables[0]),
              caption,
            })
          : entry('buttons', index, '', {
              number: String(index + 1),
              label: caption,
              example,
            })
      );
    });

    const list = [
      ...headerKeys.map(key =>
        entry('header', key, header.text, {
          caption: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.HEADER_CAPTION'),
        })
      ),
      ...Object.keys(scaffold.body || {}).map(key =>
        entry('body', key, bodyText.value)
      ),
      ...buttonEntries,
    ];
    list.forEach(linkToSpreadsheet);
    variables.value = list;
  }
);

watch(isUploadAudience, isUpload => {
  if (isUpload) variables.value.forEach(linkToSpreadsheet);
});

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);

const liquidOf = variable =>
  variableToLiquid({
    ...variable,
    column: normalizeColumnName(variable.column),
  });

const processedParams = computed(() => {
  if (!selectedTemplate.value) return {};

  const params = buildTemplateParameters(selectedTemplate.value);
  if (mediaFormat.value) {
    params.header.media_url = state.mediaUrl;
    if (mediaFormat.value === 'document') {
      params.header.media_name = state.mediaName;
    }
  }
  variables.value.forEach(variable => {
    if (variable.section === 'buttons') {
      params.buttons[variable.key].parameter = liquidOf(variable);
    } else {
      params[variable.section][variable.key] = liquidOf(variable);
    }
  });
  return params;
});

const templateParams = computed(() => {
  const template = selectedTemplate.value;
  if (!template) return null;
  return {
    name: template.name,
    namespace: template.namespace || '',
    category: template.category || 'UTILITY',
    language: template.language || 'en_US',
    processed_params: processedParams.value,
  };
});

// The spreadsheet's columns after phone and name: one per variable that reads a column, with the
// template's example and the sentence it fills. A plain-text message has no examples to offer.
const spreadsheetColumns = computed(() => {
  if (isEvolutionInbox.value) {
    return columnsInLiquid([state.message]).map(name => ({
      name,
      description: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.USED_IN_MESSAGE'),
    }));
  }
  const columns = variables.value
    .filter(
      variable => variable.source === VARIABLE_SOURCES.COLUMN && variable.column
    )
    .map(variable => ({
      name: normalizeColumnName(variable.column),
      variable:
        variable.section === 'buttons'
          ? t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.BUTTON_CHIP', {
              variable: variable.label,
            })
          : variable.label,
      example: variable.example,
      description: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.COLUMN_DESCRIPTION', {
        variable: variable.label,
        text: `${variable.before}${variable.label}${variable.after}`,
      }),
    }));
  return columns.filter(
    (column, index) =>
      columns.findIndex(other => other.name === column.name) === index
  );
});

const audience = computed(() =>
  buildCampaignAudience({
    audienceType: state.audienceType,
    selectedLabels: state.selectedLabels,
    inboxId: state.selectedAudienceInboxId,
    contactIds: state.uploadedContactIds,
  })
);

// Everything the preview renders, and nothing else: the title is typed letter by letter and does
// not change what goes out.
const previewBody = computed(() => {
  if (!state.inboxId || !audience.value.length) return null;
  if (isEvolutionInbox.value) {
    return state.message.trim()
      ? {
          inbox_id: state.inboxId,
          audience: audience.value,
          message: state.message,
        }
      : null;
  }
  return templateParams.value
    ? {
        inbox_id: state.inboxId,
        audience: audience.value,
        template_params: templateParams.value,
      }
    : null;
});

const preview = ref(null);
const isPreviewLoading = ref(false);
let previewTimer = null;
let previewRequest = 0;

const fetchPreview = async body => {
  previewRequest += 1;
  const request = previewRequest;
  isPreviewLoading.value = true;
  try {
    const { data } = await CampaignsAPI.messagePreview(body);
    if (request === previewRequest) preview.value = data;
  } catch (error) {
    if (request === previewRequest) preview.value = null;
  } finally {
    if (request === previewRequest) isPreviewLoading.value = false;
  }
};

watch(previewBody, body => {
  clearTimeout(previewTimer);
  if (!body) {
    previewRequest += 1;
    preview.value = null;
    isPreviewLoading.value = false;
    return;
  }
  previewTimer = setTimeout(() => fetchPreview(body), PREVIEW_DEBOUNCE_MS);
});

// What fills each variable, for the preview to show before there is anyone to render it for.
const summaries = computed(() => {
  if (isEvolutionInbox.value) {
    return Object.fromEntries(
      splitVariables(state.message)
        .filter(segment => 'key' in segment)
        .map(({ key }) => {
          const field = key.match(/^contact\.(\w+)$/)?.[1];
          const column = key.match(/^contact\.custom_attribute\.(\w+)$/)?.[1];
          const label =
            (field && sourceLabel(field)) || (column && columnChip(column));
          return [`message.${key}`, label || key];
        })
    );
  }
  return Object.fromEntries(
    variables.value.map(variable => {
      if (!liquidOf(variable)) return [variable.id, ''];
      if (variable.source === VARIABLE_SOURCES.TEXT) {
        return [variable.id, variable.text];
      }
      if (variable.source === VARIABLE_SOURCES.COLUMN) {
        return [variable.id, columnChip(normalizeColumnName(variable.column))];
      }
      return [variable.id, sourceLabel(variable.source)];
    })
  );
});

// The contact fields and the spreadsheet's columns, to drop into an Evolution message at the cursor.
const messageSuggestions = computed(() => [
  ...[
    VARIABLE_SOURCES.NAME,
    VARIABLE_SOURCES.FIRST_NAME,
    VARIABLE_SOURCES.PHONE,
    VARIABLE_SOURCES.EMAIL,
  ].map(source => ({
    label: sourceLabel(source),
    value: variableToLiquid({ source }),
  })),
  ...state.uploadedColumns.map(column => ({
    label: columnChip(column),
    value: variableToLiquid({ source: VARIABLE_SOURCES.COLUMN, column }),
  })),
]);

const insertMessageVariable = (value, placeholder = '') => {
  const textarea = messageTextAreaRef.value?.textareaRef;
  const start = textarea?.selectionStart ?? state.message.length;
  const end = textarea?.selectionEnd ?? state.message.length;
  state.message =
    state.message.slice(0, start) + value + state.message.slice(end);
  if (!placeholder || !textarea) return;

  // A new column has no name yet: leave its placeholder selected, so typing names it.
  nextTick(() => {
    const at = start + value.indexOf(placeholder);
    textarea.focus();
    textarea.setSelectionRange(at, at + placeholder.length);
  });
};

const insertNewColumn = () => {
  const placeholder = t(
    'CAMPAIGN.WHATSAPP.CREATE.VARIABLES.NEW_COLUMN_PLACEHOLDER'
  );
  insertMessageVariable(
    variableToLiquid({ source: VARIABLE_SOURCES.COLUMN, column: placeholder }),
    placeholder
  );
};

// What still stands between the form and a campaign, listed instead of a button that is simply off.
const missing = computed(() => {
  const items = [];
  if (!state.title.trim()) {
    items.push(t('CAMPAIGN.WHATSAPP.CREATE.MISSING.TITLE'));
  }
  if (!state.inboxId) {
    items.push(t('CAMPAIGN.WHATSAPP.CREATE.MISSING.INBOX'));
  } else if (isEvolutionInbox.value) {
    if (!state.message.trim()) {
      items.push(t('CAMPAIGN.WHATSAPP.CREATE.MISSING.MESSAGE'));
    }
  } else if (!selectedTemplate.value) {
    items.push(t('CAMPAIGN.WHATSAPP.CREATE.MISSING.TEMPLATE'));
  } else {
    if (mediaFormat.value && !state.mediaUrl.trim()) {
      items.push(t('CAMPAIGN.WHATSAPP.CREATE.MISSING.MEDIA'));
    }
    variables.value
      .filter(variable => !liquidOf(variable))
      .forEach(variable =>
        items.push(
          t('CAMPAIGN.WHATSAPP.CREATE.MISSING.VARIABLE', {
            variable: variable.label,
          })
        )
      );
  }
  if (!audience.value.length) {
    items.push(t('CAMPAIGN.WHATSAPP.CREATE.MISSING.AUDIENCE'));
  }
  return items;
});

const isCreating = computed(() => uiFlags.value.isCreating);

const campaignDetails = () => {
  const details = {
    title: state.title,
    inbox_id: state.inboxId,
    scheduled_at: state.scheduledAt
      ? new Date(state.scheduledAt).toISOString()
      : null,
    audience: audience.value,
  };
  if (isEvolutionInbox.value) return { ...details, message: state.message };

  return {
    ...details,
    message: renderTemplatePreview(
      bodyText.value,
      processedParams.value.body || {}
    ),
    template_params: templateParams.value,
  };
};

const goBack = () => router.push({ name: 'campaigns_whatsapp_index' });

const createCampaign = async () => {
  try {
    await store.dispatch('campaigns/create', campaignDetails());
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    goBack();
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE'));
  }
};

const tips = computed(() => [
  {
    icon: 'i-lucide-user',
    title: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TIPS.CONTACT.TITLE'),
    text: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TIPS.CONTACT.TEXT'),
  },
  {
    icon: 'i-lucide-file-spreadsheet',
    title: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TIPS.COLUMN.TITLE'),
    text: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TIPS.COLUMN.TEXT'),
  },
  {
    icon: 'i-lucide-type',
    title: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TIPS.TEXT.TITLE'),
    text: t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TIPS.TEXT.TEXT'),
  },
]);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto">
    <header
      class="sticky top-0 z-10 px-6 border-b border-n-weak bg-n-surface-1"
    >
      <div class="flex items-center w-full max-w-6xl gap-3 py-4 mx-auto">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          type="button"
          @click="goBack"
        />
        <div class="flex flex-col min-w-0">
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.TITLE') }}
          </h1>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.SUBTITLE') }}
          </p>
        </div>
      </div>
    </header>

    <main class="px-6 py-6">
      <div
        class="grid items-start w-full max-w-6xl gap-6 mx-auto lg:grid-cols-[minmax(0,1fr)_380px]"
      >
        <div class="flex flex-col gap-6">
          <CampaignFormSection
            :step="1"
            :title="t('CAMPAIGN.WHATSAPP.CREATE.SECTIONS.MESSAGE.TITLE')"
            :description="
              t('CAMPAIGN.WHATSAPP.CREATE.SECTIONS.MESSAGE.DESCRIPTION')
            "
          >
            <Input
              v-model="state.title"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')
              "
            />
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
                </label>
                <ComboBox
                  v-model="state.inboxId"
                  :options="inboxOptions"
                  :placeholder="
                    t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')
                  "
                  :class="comboBoxClass"
                />
              </div>
              <div v-if="!isEvolutionInbox" class="flex flex-col gap-1">
                <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
                </label>
                <ComboBox
                  v-model="state.templateId"
                  :options="templateOptions"
                  :disabled="!state.inboxId"
                  :placeholder="
                    t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')
                  "
                  :class="comboBoxClass"
                />
              </div>
            </div>

            <template v-if="mediaFormat">
              <Input
                v-model="state.mediaUrl"
                type="url"
                :label="
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.LABEL', {
                    format: mediaFormat,
                  })
                "
                :placeholder="
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.PLACEHOLDER')
                "
              />
              <Input
                v-if="mediaFormat === 'document'"
                v-model="state.mediaName"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
                "
              />
            </template>

            <div v-if="isEvolutionInbox" class="flex flex-col gap-2">
              <TextArea
                ref="messageTextAreaRef"
                v-model="state.message"
                :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.LABEL')"
                :placeholder="
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.PLACEHOLDER')
                "
                :max-length="4096"
                show-character-count
                auto-height
              />
              <div class="flex flex-wrap items-center gap-1.5">
                <span class="text-xs text-n-slate-11">
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.INSERT') }}
                </span>
                <button
                  v-for="suggestion in messageSuggestions"
                  :key="suggestion.value"
                  type="button"
                  class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12"
                  @click="insertMessageVariable(suggestion.value)"
                >
                  {{ suggestion.label }}
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-xs rounded-md bg-n-blue-3 text-n-blue-11 hover:brightness-110"
                  @click="insertNewColumn"
                >
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.NEW_COLUMN') }}
                </button>
              </div>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.INFO') }}
              </p>
            </div>
          </CampaignFormSection>

          <CampaignFormSection
            :step="2"
            :title="t('CAMPAIGN.WHATSAPP.CREATE.SECTIONS.AUDIENCE.TITLE')"
            :description="
              t('CAMPAIGN.WHATSAPP.CREATE.SECTIONS.AUDIENCE.DESCRIPTION')
            "
          >
            <CampaignAudienceField
              v-model:audience-type="state.audienceType"
              v-model:selected-labels="state.selectedLabels"
              v-model:selected-inbox-id="state.selectedAudienceInboxId"
              v-model:uploaded-contact-ids="state.uploadedContactIds"
              v-model:uploaded-columns="state.uploadedColumns"
              :spreadsheet-columns="spreadsheetColumns"
              :template-file-name="
                selectedTemplate ? `${selectedTemplate.name}.xlsx` : undefined
              "
              :label-options="labelOptions"
              :inbox-options="audienceInboxOptions"
            />
          </CampaignFormSection>

          <CampaignFormSection
            v-if="variables.length"
            :step="3"
            :title="t('CAMPAIGN.WHATSAPP.CREATE.SECTIONS.VARIABLES.TITLE')"
            :description="
              t(
                'CAMPAIGN.WHATSAPP.CREATE.SECTIONS.VARIABLES.DESCRIPTION',
                { count: variables.length },
                variables.length
              )
            "
          >
            <ul class="grid gap-2 mb-0 sm:grid-cols-3">
              <li
                v-for="tip in tips"
                :key="tip.icon"
                class="flex flex-col gap-1 p-3 list-none rounded-lg bg-n-alpha-1"
              >
                <span
                  class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
                >
                  <Icon :icon="tip.icon" class="size-4 text-n-slate-11" />
                  {{ tip.title }}
                </span>
                <span class="text-xs text-n-slate-11">{{ tip.text }}</span>
              </li>
            </ul>
            <p
              v-if="isUploadAudience"
              class="p-2.5 mb-0 text-xs rounded-md bg-n-blue-3 text-n-blue-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.UPLOAD_HINT') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.EMPTY_RULE') }}
            </p>

            <WhatsAppCampaignVariable
              v-for="variable in variables"
              :key="variable.id"
              v-model:source="variable.source"
              v-model:column="variable.column"
              v-model:text="variable.text"
              :label="variable.label"
              :caption="variable.caption"
              :before="variable.before"
              :after="variable.after"
              :source-options="sourceOptions"
              :uploaded-columns="state.uploadedColumns"
            />
          </CampaignFormSection>

          <CampaignFormSection
            :step="variables.length ? 4 : 3"
            :title="t('CAMPAIGN.WHATSAPP.CREATE.SECTIONS.SCHEDULE.TITLE')"
          >
            <CampaignScheduleField v-model="state.scheduledAt" />
          </CampaignFormSection>
        </div>

        <aside class="flex flex-col gap-4 lg:sticky lg:top-24">
          <WhatsAppCampaignPreview
            :template="isEvolutionInbox ? null : selectedTemplate"
            :message="isEvolutionInbox ? state.message : ''"
            :summaries="summaries"
            :preview="preview"
            :is-loading="isPreviewLoading"
          />
          <div class="flex flex-col gap-3">
            <Button
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
              class="w-full"
              type="button"
              :is-loading="isCreating"
              :disabled="isCreating || missing.length > 0"
              @click="createCampaign"
            />
            <div
              v-if="missing.length"
              class="flex flex-col gap-1 text-xs text-n-slate-11"
            >
              <span>{{ t('CAMPAIGN.WHATSAPP.CREATE.MISSING.HEADING') }}</span>
              <ul class="flex flex-col gap-0.5 pl-4 mb-0 list-disc">
                <li v-for="item in missing" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </main>
  </div>
</template>
